import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { ICON_MAP, ICON_LIST, letterGlow } from "../lib/dailyIcon";
import { Sparkles } from "lucide-react";

// v109.9.91 — Daily icon picker shown right after a successful Punch-In.
// The user picks one icon from ICON_LIST; the backend persists it for the
// current IST date and rejects further changes for the day.
export default function DailyIconPickerDialog({ open, onOpenChange, userName, onPicked }) {
  const [pending, setPending] = useState(null);   // icon key currently highlighted
  const [saving, setSaving] = useState(false);
  const g = letterGlow(userName);

  const confirm = async () => {
    if (!pending || saving) return;
    setSaving(true);
    try {
      await api.post("/me/daily-icon", { icon: pending });
      toast.success("Your logo for today is set");
      if (onPicked) onPicked(pending);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="daily-icon-picker">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <Sparkles size={16} className="text-amber-500" />
            Pick your logo for today
          </DialogTitle>
          <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-1">
            You can pick one per day — it will appear as your avatar across the app.
          </div>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-2.5 py-2" data-testid="daily-icon-grid">
          {ICON_LIST.map((key) => {
            const Icon = ICON_MAP[key];
            const active = pending === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setPending(key)}
                className={`group relative aspect-square rounded-2xl grid place-items-center border-2 transition-all ${
                  active
                    ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/15 scale-[1.04]"
                    : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-indigo-300 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                }`}
                data-testid={`daily-icon-opt-${key}`}
                aria-pressed={active}
              >
                <Icon
                  size={26}
                  className={g.text.split(" ")[0]}
                  style={{ filter: `drop-shadow(0 0 10px ${g.glow})` }}
                />
                {active && (
                  <span
                    className="absolute inset-0 rounded-2xl pointer-events-none ring-2 ring-indigo-400/60"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="daily-icon-skip"
          >
            Skip
          </Button>
          <Button
            onClick={confirm}
            disabled={!pending || saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="daily-icon-confirm"
          >
            {saving ? "Saving…" : "Use this logo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
